import React, {FC} from 'react';

import Section from '../../components/section';
import {PageHero} from '../../components/page-hero';
import Layout from '../../components/layout/layout';

import {IntegrationBotProps} from '../../types';
import IntegrationSteps from '../integrationStep';

import favicon from '../../images/favicon.png';
import plug from '../../images/integrations/plug.svg';
import export1 from '../../images/integrations/integration-step/export1-min.png';
import export2 from '../../images/integrations/integration-step/export2-min.png';
import export3 from '../../images/integrations/integration-step/export3-min.png';
import export4 from '../../images/integrations/integration-step/export4-min.png';
import export5 from '../../images/integrations/integration-step/export5-min.png';
import drive1 from '../../images/integrations/integration-step/drive1-min.png';
import drive2 from '../../images/integrations/integration-step/drive2-min.png';
import drive3 from '../../images/integrations/integration-step/drive3-min.png';
import drive4 from '../../images/integrations/integration-step/drive4-min.png';
import drive5 from '../../images/integrations/integration-step/drive5-min.png';
import import1 from '../../images/integrations/integration-step/import1-min.png';
import import2 from '../../images/integrations/integration-step/import2-min.png';
import import3 from '../../images/integrations/integration-step/import3-min.png';
import import4 from '../../images/integrations/integration-step/import4-min.png';
import import5 from '../../images/integrations/integration-step/import5-min.png';

import './styles.scss';

const botNames = {
  GOOGLE: 'Google',
  TELEGRAM: 'Telegram',
  SLACK: 'Slack',
  DISCORD: 'Discord',
  PABBLY_CONNECT: 'Pabbly Connect',
  ZAPIER: 'Zapier',
  KONNECTZIT: 'KonnectzIT',
  MAKE: 'Make, formerly Integromat',
  INTEGRATELY: 'Integrately',
  MS_TEAMS: 'Microsoft Teams',
  WORDPRESS: 'WordPress',
  SQL_DATABASE: 'SQL Database',
  GOOGLE_DRIVE: 'Google Drive',
  G_SHEETS_EXPORT: 'Google Sheets (Export / Sync)',
  G_SHEETS_IMPORT: 'Google Sheets Import',
};

const IntegrationBot: FC<IntegrationBotProps> = ({botName, botLogo}) => (
  <Layout>
    <PageHero
      title={`Hexomatic Integration with ${botName}`}
      subtitle={
        botName === botNames.GOOGLE ? (
          <p>Seamlessly login via your Google account</p>
        ) : botName === botNames.SLACK || botName === botNames.DISCORD || botName === botNames.TELEGRAM ? (
          <p>Send and get notifications in {botName} with Hexomatic</p>
        ) : botName === botNames.MS_TEAMS ? (
          <p>Get notifications in Microsoft Teams about workflow status with Hexomatic</p>
        ) : botName === botNames.G_SHEETS_IMPORT ? (
          <p>Import data via Google Sheets</p>
        ) : botName === botNames.G_SHEETS_EXPORT ? (
          <p>Export and synchronize data in your workflow via Google Sheets</p>
        ) : botName === botNames.SQL_DATABASE ? (
          <p>Get data from any SQL database inside your workflow</p>
        ) : botName === botNames.WORDPRESS ? (
          <p>Create posts at scale in WordPress</p>
        ) : botName === botNames.PABBLY_CONNECT ||
          botName === botNames.ZAPIER ||
          botName === botNames.MAKE ||
          botName === botNames.KONNECTZIT ||
          botName === botNames.INTEGRATELY ? (
          <p>Tap into {botName} to connect your Hexomatic workflow to hundreds of apps.</p>
        ) : botName === botNames.GOOGLE_DRIVE ? (
          <p>Export or sync your data to Google Drive</p>
        ) : (
          <p>Tap into {botName} to connect your Hexomatic workflow to thousands of apps.</p>
        )
      }
      titleSmall
      noPaddingBottom
    />
    <Section>
      <div className="col-xs-12 col-lg-12 d-flex justify-content-center">
        <img src={favicon} alt="Hexomatic" width={120} height={120} className="mr-2" />
        <img src={plug} alt="plug" width={60} height={60} className="ml-2 mr-2 mt_30" />
        <img src={botLogo} alt={botName} width={120} height={120} className="ml-2" />
      </div>
      <div className="col-md-12 pt_50">
        <div className="steps-container">
          {botName === botNames.TELEGRAM && (
            <>
              {' '}
              <p>
                Telegram is known as a cross-platform messaging application that is commonly used for offering enhanced
                privacy and encryption features.
              </p>
              <p>
                To connect Telegram to Hexomatic, be sure to have the Telegram application installed on your device
                before clicking on connect.
              </p>
            </>
          )}

          {botName === botNames.GOOGLE_DRIVE && (
            <>
              {' '}
              <p>
                Google Drive is a cloud-based storage solution enabling saving files online and accessing them anywhere
                from any computer, smartphone, or tablet.
              </p>
              <p>To connect Google Drive to Hexomatic, be sure to have your Google account integrated.</p>
              <p>
                Add Google Drive (Export / Sync) automation per workflow. Each time workflow runs at the specified point
                the workflow data will be sent to your Google Drive.
              </p>
              <p>To use this automation in a workflow, follow the steps below:</p>
            </>
          )}
          {botName === (botNames.G_SHEETS_EXPORT && botNames.G_SHEETS_IMPORT) && (
            <p>
              Google Sheets is a web-based application, allowing users to create, update and modify spreadsheets. In
              contrast to other spreadsheets, it’s totally free and allows sharing data online in real-time.
            </p>
          )}

          {botName === botNames.G_SHEETS_IMPORT && (
            <>
              <p>To integrate Google Sheets to Hexomatic, be sure to have your Google Account integrated.</p>
              <p>Before starting, add Google Sheets Import automation as a starting point of the workflow.</p>
              <p>Follow the steps below to use the automation in a workflow:</p>
            </>
          )}
          {botName === botNames.G_SHEETS_EXPORT && (
            <>
              <p>
                The Google Sheets (Export / Sync) automation enables you to export or sync data from your workflow to
                new or existing Google Sheets.
              </p>
              <p>To use the automation in a workflow, follow the steps below:</p>
            </>
          )}
          {botName === botNames.SLACK && (
            <>
              <p>Slack is an instant messaging tool that provides unified workplace communication.</p>
              <p>
                To connect Slack to Hexomatic, be sure to have the Slack application installed on your device before
                clicking on connect.
              </p>
            </>
          )}
          {botName === botNames.GOOGLE && (
            <>
              <p>
                Your Google Account grants you access to a wide range of its products, including Gmail, Google docs,
                Google Sheets, Google Ads, and more using the same username and password.
              </p>
              <p>Hexomatic allows you to login using your Google account for 1-click login without passwords.</p>
              <p>Follow the steps below, to connect your Hexomatic workflow with your Google account.</p>
            </>
          )}
          {botName === botNames.DISCORD && (
            <>
              <p>
                Discord is a communications platform enabling the creation of private servers where users can generate
                dedicated channels and join various servers with a single account.
              </p>
              <p>
                To connect Discord to Hexomatic, be sure to have the Discord application installed on your device before
                clicking on connect.
              </p>
            </>
          )}
          {botName === botNames.PABBLY_CONNECT && (
            <>
              <p>
                Pabbly Connect is an integration platform that supports 300+ applications including marketing, sales,
                eCommerce, CRM, and payment apps. Our integration enables you to send data to hundreds of compatible
                apps in the Pabbly Connect ecosystem.
              </p>
            </>
          )}
          {botName === botNames.ZAPIER && (
            <>
              <p>
                Zapier is an integration platform that supports 3000+ applications including marketing, sales,
                eCommerce, CRM, and payment apps. Our integration enables you to send data to hundreds of compatible
                apps in the Zapier ecosystem.
              </p>
              <p>To connect Zapier to Hexomatic, be sure to have your Zapier API key copied from the Settings page.</p>
            </>
          )}
          {(botName === botNames.KONNECTZIT || botName === botNames.INTEGRATELY) && (
            <>
              {' '}
              <p>
                {botName} is an integration platform that supports hundreds of applications including marketing, sales,
                eCommerce, CRM, and payment apps. Our integration enables you to send workflow data to hundreds of
                compatible apps in the {botName} ecosystem.
              </p>
            </>
          )}
          {botName === botNames.MAKE && (
            <>
              {' '}
              <p>
                Make is an integration platform that supports hundreds of applications including marketing, sales,
                eCommerce, CRM and payment apps. Our integration enables you to send workflow data to hundreds of
                compatible apps in the Make ecosystem.
              </p>
            </>
          )}
          {botName === botNames.MS_TEAMS && (
            <>
              {' '}
              <p>
                Microsoft Teams is a business communication platform developed by Microsoft, helping to bring people
                together. It can be used for bringing together teammates on a project or simply discussing your work
                schedule. According to user reviews, Teams streamlines work and helps to save time.
              </p>
              <p>
                To connect Microsoft Teams to Hexomatic, be sure to have the Microsoft Teams application installed on
                your device before clicking on connect.
              </p>
            </>
          )}
          {botName === botNames.WORDPRESS && (
            <>
              {' '}
              <p>
                WordPress tops the list of the three most frequently used site-building platforms across the world. It
                has a ton of useful features that make publishing content easier than ever.
              </p>
            </>
          )}
          {botName === botNames.SQL_DATABASE && (
            <>
              {' '}
              <p>
                Tap into SQL databases from your own applications right inside Hexomatic to access fresh data at scale.
              </p>
            </>
          )}
          {botName !== botNames.GOOGLE_DRIVE &&
          botName !== botNames.G_SHEETS_EXPORT &&
          botName !== botNames.G_SHEETS_IMPORT ? (
            <IntegrationSteps
              list={[
                {
                  text:
                    botName === botNames.ZAPIER ? (
                      <>Choose a workflow</>
                    ) : (
                      <>
                        Log in at{' '}
                        <a
                          className="hexomatic-href"
                          href="https://dash.hexomatic.com"
                          aria-label={'https://dash.hexomatic.com'}
                          no-index
                        >
                          <b> dash.hexomatic.com</b>
                        </a>
                      </>
                    ),
                  key: 'Log in',
                },
                {
                  text:
                    botName === botNames.ZAPIER ? (
                      <>Click on add new automation icon and search for Zapier integration</>
                    ) : (
                      <>
                        Go to the <b>settings page</b>
                      </>
                    ),
                  key: 'Settings',
                },
                {
                  text:
                    botName === botNames.MAKE ? (
                      <>Copy your Make, formerly Integromat API key</>
                    ) : botName === botNames.INTEGRATELY ? (
                      <>Copy your {botName} API key</>
                    ) : botName === botNames.ZAPIER ? (
                      <>Click on connect</>
                    ) : (
                      <>
                        Click <b> &#60;connect with {botName} &#62; </b>
                      </>
                    ),
                  key: 'Click connect',
                },
                {
                  text:
                    botName === botNames.DISCORD || botName === botNames.MS_TEAMS || botName === botNames.GOOGLE ? (
                      <>Sign in to your {botName} account</>
                    ) : botName === botNames.TELEGRAM ? (
                      <>Open the Telegram desktop app</>
                    ) : botName === botNames.SLACK ? (
                      <>Sign in to your workspace</>
                    ) : botName === botNames.MAKE ? (
                      <>Sign in to your Make or Integromat account</>
                    ) : botName === botNames.ZAPIER ? (
                      <>Create a Zap on the redirected page </>
                    ) : botName === botNames.INTEGRATELY || botName === botNames.KONNECTZIT ? (
                      <>Sign in to your {botName} account</>
                    ) : botName === botNames.SQL_DATABASE || botName === botNames.WORDPRESS ? (
                      <>Fill in the required fields</>
                    ) : botName === botNames.PABBLY_CONNECT ? (
                      <>Sign in to your {botName} account</>
                    ) : (
                      ''
                    ),
                  key: 'Webhook',
                },
                {
                  text:
                    botName === botNames.SLACK ? (
                      <>Select the desired channel to receive notifications</>
                    ) : botName === botNames.DISCORD ? (
                      <>Select the desired Server and Authorize</>
                    ) : botName === botNames.TELEGRAM ? (
                      <>Send message on the HexomaticBot channel</>
                    ) : botName === botNames.MS_TEAMS ? (
                      <>Go to the APPs</>
                    ) : botName === botNames.MAKE ? (
                      <>Create a new scenario and search for Hexomatic</>
                    ) : botName === botNames.ZAPIER ? (
                      <>Click on Hexomatic and “Sign in to Hexomatic”</>
                    ) : botName === botNames.INTEGRATELY ? (
                      <>Search for Hexomatic in Apps and create an automation</>
                    ) : botName === botNames.PABBLY_CONNECT ? (
                      <>Create a workflow and search for “Webhook by Pabbly”</>
                    ) : botName === botNames.KONNECTZIT ? (
                      <>Create Konnectz and search for Hexomatic</>
                    ) : botName === botNames.GOOGLE ? (
                      <>Allow access to your Google Account</>
                    ) : (
                      ''
                    ),
                  key: 'Select channel',
                },
                {
                  text:
                    botName === botNames.MS_TEAMS ? (
                      <>Search for "Incoming Webhooks"</>
                    ) : botName === botNames.MAKE ? (
                      <>Select action type, create a webhook and set a connection</>
                    ) : botName === botNames.ZAPIER ? (
                      <>Enter your Hexomatic account email and paste your API key</>
                    ) : botName === botNames.INTEGRATELY ? (
                      <>Click on Add Connection and Paste your API key</>
                    ) : botName === botNames.PABBLY_CONNECT || botName === botNames.KONNECTZIT ? (
                      <>Copy the webhook URL and add it under the webhook section in Hexomatic</>
                    ) : (
                      ``
                    ),
                  key: 'Webhook',
                },
                {
                  text:
                    botName === botNames.MS_TEAMS ? (
                      <>"Add to a Team" and "Set up a connector"</>
                    ) : botName === botNames.MAKE ? (
                      <>Type in the API key and save</>
                    ) : botName === botNames.ZAPIER ? (
                      <>Press continue and set up a trigger</>
                    ) : (
                      ``
                    ),
                  key: 'Step 7',
                },
                {
                  text:
                    botName === botNames.MS_TEAMS ? (
                      <>Copy the generated Webhook URL and paste it under the webhook section in Hexomatic</>
                    ) : (
                      ``
                    ),
                  key: 'Step 8',
                },
              ]}
            />
          ) : botName === botNames.G_SHEETS_EXPORT ? (
            <div className="google-sheets">
              <ul>
                <li>
                  <p>1. Choose a workflow</p>
                  <img src={export1} alt="workflow-google-sheets-1" />
                </li>
                <li>
                  <p>2. Click on add new automation icon</p>
                  <img src={export2} alt="workflow-google-sheets-2" />
                </li>
                <li>
                  <p>3. Search for Google Sheets (Export / Sync) automation and select it</p>
                  <img src={export3} alt="workflow-google-sheets-3" />
                </li>
                <li>
                  <p>4. Select a Google Account</p>
                  <img src={export4} alt="workflow-google-sheets-4" />
                </li>
                <li>
                  <p>5. Select an existing or create a new spreadsheet</p>
                  <img src={export5} alt="workflow-google-sheets-5" />
                </li>
              </ul>
            </div>
          ) : botName === botNames.G_SHEETS_IMPORT ? (
            <div className="google-sheets">
              <ul>
                <li>
                  <p>1. Create a workflow</p>
                  <img src={import1} alt="workflow-google-sheets-6" />
                </li>
                <li>
                  <p>2. In the search bar, search for Google Sheets Import automation</p>
                  <img src={import2} alt="workflow-google-sheets-7" />
                </li>
                <li>
                  <p>3. Select a Google Account</p>
                  <img src={import3} alt="workflow-google-sheets-8" />
                </li>
                <li>
                  <p>4. Select an existing spreadsheet</p>
                  <img src={import4} alt="workflow-google-sheets-9" />
                </li>
                <li>
                  <p>5. Select a sheet and a column to be imported </p>
                  <img src={import5} alt="workflow-google-sheets-10" />
                </li>
              </ul>
            </div>
          ) : botName === botNames.GOOGLE_DRIVE ? (
            <div className="google-sheets">
              <ul>
                <li>
                  <p>1. Choose a workflow</p>
                  <img src={drive1} alt="workflow-google-drive-1" />
                </li>
                <li>
                  <p>2. Click on add new automation icon</p>
                  <img src={drive2} alt="workflow-google-drive-2" />
                </li>
                <li>
                  <p>3. Search for Google Drive (Export / Sync) automation and select it</p>
                  <img src={drive3} alt="workflow-google-drive-3" />
                </li>
                <li>
                  <p>4. Select a Google Account</p>
                  <img src={drive4} alt="workflow-google-drive-4" />
                </li>
                <li>
                  <p>5. Select an existing or create a new folder</p>
                  <img src={drive5} alt="workflow-google-drive-5" />
                </li>
              </ul>
            </div>
          ) : (
            ``
          )}
        </div>

        {botName === botNames.SLACK && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Keep your teams notified in {botName}</h3>
            <p>
              With our Hexomatic integration, you can keep your team up to date with data from your workflow and
              completion notifications.
            </p>
          </div>
        )}
        {botName === botNames.GOOGLE_DRIVE && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Save your work in {botName}</h3>
            <p>Hexomatic enables you to save any file from your workflow to your Google Drive account in minutes.</p>
          </div>
        )}
        {botName === botNames.TELEGRAM && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Automate {botName} messages with Hexomatic</h3>
            <p>Hexomatic allows you to send and get notifications in your Telegram at scale just in a few clicks.</p>
          </div>
        )}
        {botName === botNames.DISCORD && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Keep notified in {botName}</h3>
            <p>
              Our Discord integration allows you to send and receive alerts from your workflows directly into your
              Discord channel.
            </p>
          </div>
        )}
        {(botName === botNames.PABBLY_CONNECT || botName === botNames.ZAPIER || botName === botNames.KONNECTZIT) && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Take your data anywhere with {botName}</h3>
            <p>
              Our {botName} integration allows you to combine your Hexomatic workflows with {botName} enabling you to
              send data from your workflow to 100s of compatible apps on autopilot.
            </p>
          </div>
        )}
        {botName === botNames.MAKE && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Take your data anywhere with Make</h3>
            <p>
              Our Make integration allows you to combine your Hexomatic workflows with Make enabling you to send data
              from your workflow to 100s of compatible apps on autopilot.
            </p>
          </div>
        )}
        {botName === botNames.INTEGRATELY && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">
              Automate your tasks connecting Hexomatic with Integrately
            </h3>
            <p>
              Hexomatic enables you to connect to Integrately and send data from your workflow to 100s of compatible
              apps on autopilot.
            </p>
            <p>
              Using our integration you will be notified about workflow status at any point of the workflow process.
            </p>
          </div>
        )}
        {botName === botNames.MS_TEAMS && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">
              Send data and get notifications in your Microsoft Teams space with Hexomatic
            </h3>
            <p>
              Hexomatic allows you to keep your team updated in real-time using Microsoft Teams integration. It enables
              sending data and receiving notifications when your workflow completes.
            </p>
          </div>
        )}
        {botName === botNames.SQL_DATABASE && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Input data from your SQL database</h3>
            <p>
              Hexomatic allows you to get data from any SQL database inside your workflow and use this as a starting
              point for scraping recipes or automations.
            </p>
          </div>
        )}
        {botName === botNames.WORDPRESS && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Post in WordPress right from your workflow</h3>
            <p>
              Hexomatic enables you to automate WordPress posting, a time-consuming process for marketers and content
              writers who publish at scale.
            </p>
            <p>
              You can easily post in WordPress at scale from any data source from your workflow with our Hexomatic
              WordPress automation.
            </p>
          </div>
        )}
        {botName === botNames.G_SHEETS_IMPORT && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">
              Keep Google Sheets right inside your workflow{' '}
            </h3>
            <p>
              Web scraping has never been easier. You don’t have to do the tedious job of manually copying and pasting
              hundreds of entries anymore. Hexomatic allows you to use your Google sheets right inside your workflow as
              an input for scraping recipes or automations.
            </p>
          </div>
        )}
        {botName === botNames.G_SHEETS_EXPORT && (
          <div className="mt-4">
            <h3 className="f_size_24 f_600 mb_20 text-center t_color">Automate your work with Google Sheets</h3>
            <p>
              Google Sheets allows running any mathematical analysis on the data, creating charts and visualizations to
              share with others. This is super important, especially when it is necessary to share reports with
              stakeholders, team members, or business partners.
            </p>
          </div>
        )}
        <p>
          Create your own bots in minutes to extract data from any website and leverage 100+ ready-made automations to
          scale time-consuming tasks on autopilot with Hexomatic.
        </p>
      </div>
    </Section>
  </Layout>
);

export default IntegrationBot;
